import { Box, Flex, Text } from "@mantine/core";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import DocumentPreview from "components/ImageUploader/DocumentPreview";
import { DocumentWithId } from "types/document";

const THUMBNAIL_SIZE = 86;
const ARROW_BUTTON_WIDTH = 60;
const ARROW_GAP = 16;

interface DesktopImageViewerProps {
  slides: React.ReactNode;
  slideWidth: number;
  documents: DocumentWithId[];
  selectedIndex: number;
  onSelectedIndexChange: (index: number) => void;
  isAtStart: boolean;
  isAtEnd: boolean;
  onStep: (direction: 1 | -1) => void;
  messageText?: string;
  thumbnailsRef: React.RefObject<HTMLDivElement | null>;
}

export const DesktopImageViewer: React.FC<DesktopImageViewerProps> = ({
  slides,
  slideWidth,
  documents,
  selectedIndex,
  onSelectedIndexChange,
  isAtStart,
  isAtEnd,
  onStep,
  messageText,
  thumbnailsRef,
}) => {
  const showArrows = documents.length > 1;

  return (
    <Flex
      direction="column"
      style={{
        height: "100%",
        backgroundColor: "black",
      }}
    >
      <Flex
        direction="column"
        justify="center"
        style={{ flex: 1, minHeight: 0 }}
      >
        <Flex
          align="center"
          justify="center"
          gap={ARROW_GAP}
          style={{
            width: "100%",
            maxWidth: `calc(${slideWidth}px + ${
              (ARROW_BUTTON_WIDTH + ARROW_GAP) * 2
            }px)`,
            margin: "0 auto",
            padding: "0 16px",
          }}
        >
          <Box style={{ width: ARROW_BUTTON_WIDTH, flexShrink: 0 }}>
            {showArrows && !isAtStart && (
              <ImageSlider
                ariaLabel="Previous image"
                onClick={() => onStep(-1)}
              >
                <IconChevronLeft size={40} color="white" stroke={3} />
              </ImageSlider>
            )}
          </Box>

          <Box
            style={{
              height: "60vh",
              width: slideWidth,
              maxWidth: "100%",
              overflow: "hidden",
              flexGrow: 1,
            }}
          >
            {slides}
          </Box>

          <Box style={{ width: ARROW_BUTTON_WIDTH, flexShrink: 0 }}>
            {showArrows && !isAtEnd && (
              <ImageSlider ariaLabel="Next image" onClick={() => onStep(1)}>
                <IconChevronRight size={40} color="white" stroke={3} />
              </ImageSlider>
            )}
          </Box>
        </Flex>

        {messageText && (
          <Box
            style={{
              flexShrink: 0,
              padding: "8px 16px 16px",
              maxHeight: "30vh",
              overflowY: "auto",
              maxWidth: "70vw",
              margin: "0 auto",
            }}
          >
            <Text fz="md" c="white" ta="center">
              {messageText}
            </Text>
          </Box>
        )}

        {documents.length > 1 && (
          <Box
            ref={thumbnailsRef}
            style={{
              flexShrink: 0,
              display: "flex",
              gap: 8,
              padding: "12px 16px",
              overflowX: "auto",
              justifyContent: "center",
            }}
          >
            {documents.map((doc, i) => (
              <Box
                key={doc.id ?? i}
                onClick={() => onSelectedIndexChange(i)}
                style={{
                  flexShrink: 0,
                  cursor: "pointer",
                  border:
                    i === selectedIndex
                      ? "2px solid white"
                      : "2px solid transparent",
                  borderRadius: 4,
                }}
              >
                <DocumentPreview
                  width={THUMBNAIL_SIZE}
                  height={THUMBNAIL_SIZE}
                  crop="cover"
                  removable={false}
                  document={doc.blob}
                  isPdf={doc.blob.type === "application/pdf"}
                  borderSize={0}
                />
              </Box>
            ))}
          </Box>
        )}
      </Flex>
    </Flex>
  );
};

interface ImageSliderProps {
  children: React.ReactNode;
  onClick?: () => void;
  ariaLabel?: string;
}

const ImageSlider = ({ children, onClick, ariaLabel }: ImageSliderProps) => {
  return (
    <Box
      component="button"
      type="button"
      aria-label={ariaLabel}
      onClick={onClick}
      style={{
        width: ARROW_BUTTON_WIDTH,
        height: 60,
        borderRadius: "50%",
        backgroundColor: "rgba(255,255,255,0.15)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        border: "none",
        padding: 0,
      }}
    >
      {children}
    </Box>
  );
};
